import config from "../settings/config";
import { announceEntitlementRequired, isEntitlementRequired } from "./entitlementPrompt";
import { clearPersistedChatUnread } from "../redux/chatUnreadPersist";
import {
  endImpersonatedSession,
  getAuthToken,
  removeAuth,
} from "./authStorage";
const REG_TOKEN_KEY = "adoc_rt";
const REG_EXPIRES_KEY = "adoc_rt_expires_at";
const REG_EMAIL_KEY = "adoc_pending_email";
const ENV_KEY = "adoc_env";

/**
 * Clear all registration-related tokens from localStorage.
 */
export const clearRegistrationTokens = () => {
  localStorage.removeItem(REG_TOKEN_KEY);
  localStorage.removeItem(REG_EXPIRES_KEY);
  localStorage.removeItem(REG_EMAIL_KEY);
};

/**
 * Clear ALL auth state (bearer + registration + profile + agreements).
 * Use when tokens are known to be invalid (env mismatch, 401, etc.)
 */
export const clearAllAuthData = () => {
  // removeAuth, not localStorage: a 401 inside a support tab must end THAT
  // tab's session only, never sign the operator out of their own account.
  removeAuth("adoc_t");
  removeAuth("adoc_p");
  removeAuth("adoc_agreements");
  // A support tab whose token has lapsed must stop claiming to be one. The
  // banner renders off the impersonation marker alone, so leaving it behind
  // drops the operator on /login still labelled "Stai vedendo l'app come
  // questo utente" — which reads as a broken app rather than an expired
  // session. Cleared last, so the removeAuth calls above still resolve to
  // sessionStorage, and only ever sessionStorage: the operator's own account
  // stays signed in.
  endImpersonatedSession();
  localStorage.removeItem(ENV_KEY);
  clearRegistrationTokens();
  clearPersistedChatUnread();
};

/**
 * Returns true if the registration token (`adoc_rt`) exists but is expired.
 */
export const isRegistrationTokenExpired = () => {
  const regToken = localStorage.getItem(REG_TOKEN_KEY);
  if (!regToken) return false;

  const expiresAt = localStorage.getItem(REG_EXPIRES_KEY);
  if (!expiresAt) return false;

  return new Date(expiresAt) < new Date();
};

/**
 * Check if the current environment (API base URL) matches what was stored
 * when the token was issued. If not, the token is for a different backend.
 */
export const isEnvironmentMismatch = () => {
  const storedEnv = localStorage.getItem(ENV_KEY);
  const currentEnv = config.env[config.env.mode]?.url;

  // First time: no stored env. Save current and don't treat as mismatch.
  if (!storedEnv) {
    if (currentEnv) localStorage.setItem(ENV_KEY, currentEnv);
    return false;
  }

  return storedEnv !== currentEnv;
};

/**
 * Store the current environment URL so we can detect mismatches later.
 */
export const saveCurrentEnvironment = () => {
  const currentEnv = config.env[config.env.mode]?.url;
  if (currentEnv) localStorage.setItem(ENV_KEY, currentEnv);
};

/**
 * Run all stale-auth checks and clean up if needed.
 * Returns true if auth data was cleared (caller should treat user as logged out).
 */
export const cleanStaleAuthData = () => {
  // 1) Expired registration token
  if (isRegistrationTokenExpired()) {
    clearRegistrationTokens();
    return true;
  }

  // 2) Environment mismatch (e.g. staging token used against prod)
  if (isEnvironmentMismatch()) {
    console.log("[AuthGuard] Environment mismatch detected — clearing auth data",
      { stored: localStorage.getItem(ENV_KEY), current: config.env[config.env.mode]?.url });
    clearAllAuthData();
    // Force redirect to login/prelogin
    if (window.location.pathname !== "/" && window.location.pathname !== "/login") {
      window.location.href = "/";
    }
    return true;
  }

  // Save current environment for future mismatch checks
  saveCurrentEnvironment();

  return false;
};

// ── Global 401 interceptor ──────────────────────────────────────────────
// Wraps the native fetch so that ANY API call returning 401 (unauthenticated /
// expired token) from our backend automatically clears auth and redirects to
// /login. A 403 (authenticated but forbidden for THIS action) must NOT end the
// session — it is surfaced to the caller to handle (e.g. show an error toast).
// Only intercepts calls to our own API base URL.

let interceptorInstalled = false;

/** Shared handler for 401 — used by both fetch and axios interceptors */
const handleUnauthorized = (url) => {
  const currentBaseUrl = config.env[config.env.mode]?.url;
  if (!currentBaseUrl) return;
  if (!url?.startsWith(currentBaseUrl)) return;

  // Skip auth endpoints
  if (url.includes("/api/social-login") || url.includes("/api/v1/otp/")) return;

  // Playback telemetry is best-effort and must never be what signs a member
  // out. The endpoint answers 202 either way; this is belt and braces.
  if (url.includes("/api/v1/video-playback/")) return;

  // Only act if we have a token (avoid loops)
  if (!getAuthToken()) return;

  clearAllAuthData();

  if (window.location.pathname !== "/" && window.location.pathname !== "/login") {
    window.location.href = "/";
  }
};

/** Premium Lite: a 403 the member's plan explains → the app-wide popup. */
const announceIfEntitlementRequired = (response) => {
  // A clone, so the caller can still read the body.
  response
    .clone()
    .json()
    .then((body) => {
      if (isEntitlementRequired(403, body)) announceEntitlementRequired(body);
    })
    .catch(() => {});
};

export function installAuthInterceptor() {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  const currentBaseUrl = config.env[config.env.mode]?.url;
  if (!currentBaseUrl) return;

  // ── Fetch interceptor ──
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    const url = typeof args[0] === "string" ? args[0] : args[0]?.url ?? "";

    if (response.status === 401) {
      handleUnauthorized(url);
    }
    if (response.status === 403 && url.startsWith(currentBaseUrl)) {
      announceIfEntitlementRequired(response);
    }
    return response;
  };

  // ── Axios interceptor ──
  try {
    const axios = require("axios");
    axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url ?? "";
        if (status === 401) {
          handleUnauthorized(url);
        }
        if (isEntitlementRequired(status, error?.response?.data) && url.startsWith(currentBaseUrl)) {
          announceEntitlementRequired(error.response.data);
        }
        return Promise.reject(error);
      }
    );
  } catch {
    // axios not available — skip
  }
}
